import request from '@/utils/request';
import type {
  CalculateRentalResult,
  CreateProductData,
  Product,
  ProductBusinessModel,
  ProductQueryParams,
  ProductResponse,
  ProductSource,
  UpdateProductData,
} from '@/types/product';
import {tagService} from '@/services/tagService';

const PRODUCTS = '/api/admin/products';
const PLATFORM_PRODUCTS = '/api/admin/platform-products';
const MERCHANT_PRODUCTS = '/api/admin/merchant-products';
const LONG_PLATFORM_PRODUCTS = '/api/admin/long-rent-platform-products';
const LONG_MERCHANT_PRODUCTS = '/api/admin/long-rent-merchant-products';

function crudPath(model?: ProductBusinessModel, source?: ProductSource) {
  if (model !== 'LONG') {
    return PRODUCTS;
  }
  return source === 'PLATFORM' ? LONG_PLATFORM_PRODUCTS : LONG_MERCHANT_PRODUCTS;
}

function platformPath(model?: ProductBusinessModel) {
  return model === 'LONG' ? LONG_PLATFORM_PRODUCTS : PLATFORM_PRODUCTS;
}

function merchantPath(model?: ProductBusinessModel) {
  return model === 'LONG' ? LONG_MERCHANT_PRODUCTS : MERCHANT_PRODUCTS;
}

export interface CalendarStockUploadResult {
  successCount: number;
  skipCount: number;
  totalCount: number;
  failedProductCodes?: string[];
  notFoundProductNames?: string[];
}

export interface CalendarStockWeekItem {
  productId: number;
  productName: string;
  productCode: string;
  skuId: number;
  skuCode: string;
  skuImage?: string;
  skuLabel?: string;
  dailyStocks: (number | null)[];
}

export interface CalendarStockWeekData {
  weekStart: string;
  weekEnd: string;
  dates: string[];
  items: CalendarStockWeekItem[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

export const productService = {
  async getProducts(params: ProductQueryParams): Promise<ProductResponse> {
    const response = await request.get(crudPath(params.businessModel, params.productSource), { params });
    return response;
  },

  async getProductById(id: number, businessModel?: ProductBusinessModel, productSource?: ProductSource): Promise<Product> {
    const response = await request.get(`${crudPath(businessModel, productSource)}/${id}`);
    return response;
  },

  async getProductByCode(code: string): Promise<Product> {
    const response = await request.get(`${PRODUCTS}/by-code/${code}`);
    return response;
  },

  async calculateRental(productId: number, days: number): Promise<CalculateRentalResult> {
    const response = await request.get(`${PRODUCTS}/${productId}/calculate-rental`, {
      params: { days },
    });
    return response;
  },

  async createProduct(data: CreateProductData, businessModel?: ProductBusinessModel): Promise<Product> {
    const response = await request.post(crudPath(businessModel, data.productSource), data);
    return response;
  },

  async updateProduct(id: number, data: UpdateProductData, businessModel?: ProductBusinessModel): Promise<Product> {
    const response = await request.put(`${crudPath(businessModel, data.productSource)}/${id}`, data);
    return response;
  },

  async associateBrandModel(productId: number, brandId: number, productModelId?: number): Promise<Product> {
    const [product, tags] = await Promise.all([
      this.getProductById(productId),
      tagService.getProductTags(productId).catch(() => []),
    ]);

    const specificationValues = (product.specificationValues || []).map((sv) => ({
      id: sv.id,
      specificationId: sv.specificationId!,
      value: sv.value,
      sort: sv.sort ?? 0,
    }));

    const skus = (product.skus || []).map((sku) => ({
      id: sku.id,
      code: sku.code,
      image: sku.image || '',
      stock: sku.stock,
      marketPrice: sku.marketPrice,
      deposit: sku.deposit,
      firstDayRent: sku.firstDayRent,
      stableRent: sku.stableRent,
      threeDayRent: sku.threeDayRent,
      fiveDayRent: sku.fiveDayRent,
      sevenDayRent: sku.sevenDayRent,
      tenDayRent: sku.tenDayRent,
      fifteenDayRent: sku.fifteenDayRent,
      thirtyDayRent: sku.thirtyDayRent,
      ninetyDayRent: sku.ninetyDayRent,
      oneHundredEightyDayRent: sku.oneHundredEightyDayRent,
      threeHundredSixtyDayRent: sku.threeHundredSixtyDayRent,
      erpComboCode: sku.erpComboCode,
      specificationValues: (sku.specificationValues || []).map((sv) => ({
        id: sv.id,
        specificationId: sv.specificationId,
        specificationValueId: sv.specificationValueId,
        value: sv.value,
      })),
    }));

    return this.updateProduct(productId, {
      id: productId,
      name: product.name,
      code: product.code,
      categoryId: product.categoryId,
      brandId,
      productModelId,
      description: product.description,
      mainImage: product.mainImage,
      whiteBackgroundImage: product.whiteBackgroundImage,
      sort: product.sort,
      virtualSales: product.virtualSales,
      qualityGrade: product.qualityGrade,
      productSource: product.productSource,
      tags: tags.map((t) => t.id),
      specificationValues,
      skus,
    } as UpdateProductData);
  },

  async deletePlatformProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.delete(`${platformPath(businessModel)}/${id}`);
  },

  async deleteMerchantProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.delete(`${merchantPath(businessModel)}/${id}`);
  },

  // 删除商品分类
  deleteCategory: async (id: number): Promise<void> => {
    await request.delete(`/api/admin/product-categories/${id}`);
  },

  async onlineProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/${id}/online`);
  },

  async publishPlatformProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${platformPath(businessModel)}/${id}/publish`);
  },

  async copyMerchantProductToPlatform(id: number, businessModel?: ProductBusinessModel): Promise<Product> {
    const response = await request.post(`${platformPath(businessModel)}/copy-from-merchant/${id}`);
    return response;
  },

  async batchPublishPlatformProduct(productIds: number[], businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${platformPath(businessModel)}/batch/publish`, { productIds });
  },

  async offlinePlatformProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${platformPath(businessModel)}/${id}/offline`);
  },

  async offlineMerchantProduct(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/${id}/offline`);
  },

  async batchOfflinePlatform(productIds: number[], businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${platformPath(businessModel)}/batch/offline`, { productIds });
  },

  async batchOfflineMerchant(productIds: number[], businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/batch/offline`, { productIds });
  },

  async batchSyncToAlipay(productIds: number[], businessModel?: ProductBusinessModel): Promise<void> {
    await request.post(`${platformPath(businessModel)}/batch/sync-alipay`, { productIds });
  },

  async syncProductToAlipay(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.post(`${platformPath(businessModel)}/${id}/sync-alipay`);
  },

  async syncProductToJd(id: number): Promise<void> {
    await request.post(`/api/admin/jd-products/${id}/sync`);
  },

  async batchSyncToJd(productIds: number[]): Promise<void> {
    await request.post('/api/admin/jd-products/batch/sync', { productIds });
  },

  async onlineJdProduct(id: number): Promise<void> {
    await request.post(`/api/admin/jd-products/${id}/online`);
  },

  async offlineJdProduct(id: number): Promise<void> {
    await request.post(`/api/admin/jd-products/${id}/offline`);
  },

  async batchOnlineJd(productIds: number[]): Promise<void> {
    await request.post('/api/admin/jd-products/batch/online', { productIds });
  },

  async batchOfflineJd(productIds: number[]): Promise<void> {
    await request.post('/api/admin/jd-products/batch/offline', { productIds });
  },

  async submitProductForReview(id: number, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/${id}/submit-review`);
  },

  async reviewProduct(id: number, approved: boolean, reason?: string, businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/${id}/review`, null, {
      params: { approved, reason }
    });
  },

  async createMiniAppQrcode(code: String, businessModel?: ProductBusinessModel): Promise<string> {
    const response = await request.get(`${merchantPath(businessModel)}/qrcode/${code}`);
    return response;
  },

  async getProductOperationLogsByProductId(id: number): Promise<any[]> {
    const response = await request.get(`${PRODUCTS}/${id}/operation-logs`);
    return response;
  },

  async batchSubmitForReview(productIds: number[], businessModel?: ProductBusinessModel): Promise<void> {
    await request.put(`${merchantPath(businessModel)}/batch/submit-review`, { productIds });
  },

  async exportProducts(params: any): Promise<void> {
    try {
      const processedParams: any = { ...params };
      if (Array.isArray(processedParams.productIds)) {
        processedParams.productIds = processedParams.productIds.join(',');
      }

      const response = await request.get(`${PRODUCTS}/batch-export`, {
        params: processedParams,
        paramsSerializer: {
          indexes: null,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `商品批量导出_${new Date().getTime()}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('导出失败:', error);
      throw error;
    }
  },

  async downloadCalendarStockTemplate(categoryId?: number): Promise<void> {
    try {
      const params = categoryId ? { categoryId } : {};
      const response = await request.get(`${PRODUCTS}/batch-upload/calendar-stock/download/template`, {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '日历库存批量上传模版.xlsx';

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('下载日历库存模板失败:', error);
      throw error;
    }
  },

  async batchUploadCalendarStock(file: File): Promise<CalendarStockUploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await request.post(`${PRODUCTS}/batch-upload/calendar-stock`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  async getProductCalendarStockWeek(productId: number, weekStart?: string): Promise<CalendarStockWeekData> {
    const response = await request.get(`${PRODUCTS}/${productId}/calendar-stock/week`, {
      params: weekStart ? { weekStart } : undefined,
    });
    return response;
  },

  async downloadPlatformBatchUploadTemplate(businessModel: 'SHORT' | 'LONG' = 'SHORT'): Promise<void> {
    try {
      const response = await request.get(`${PLATFORM_PRODUCTS}/batch-upload/download/template`, {
        params: { businessModel },
        responseType: 'blob',
      });

      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = businessModel === 'LONG' ? '长租平台商品批量上传模板.xlsx' : '平台商品批量上传模板.xlsx';

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载平台商品批量上传模板失败:', error);
      throw error;
    }
  },

  async batchUploadPlatformProducts(file: File, businessModel: 'SHORT' | 'LONG' = 'SHORT'): Promise<boolean> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('businessModel', businessModel);

    const response = await request.post(`${PLATFORM_PRODUCTS}/batch-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },
};
